import { supabase } from '../../lib/supabaseClient';
import { normalizeStorageUrl } from '../../lib/content';

export const MEDIA_BUCKET = 'media';
export const APPLICATIONS_BUCKET = 'applications';
export const MAX_MEDIA_BYTES = 5 * 1024 * 1024;

// Supabase object keys (and the public URLs built from them) break when the
// file name contains accents, apostrophes or spaces: the request ends with a
// 400. Store every file under a plain ASCII name instead.
export const sanitizeFileName = (name = 'file') => {
  const dot = name.lastIndexOf('.');
  const rawBase = dot > -1 ? name.slice(0, dot) : name;
  const rawExt = dot > -1 ? name.slice(dot + 1) : '';

  const clean = (value) => value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '')
    .toLowerCase();

  const base = clean(rawBase) || 'file';
  const ext = clean(rawExt);
  return ext ? `${base}.${ext}` : base;
};

export const uploadFile = async (file, folder, bucket = MEDIA_BUCKET) => {
  const safeName = sanitizeFileName(file.name);
  const path = `${folder}/${Date.now()}-${safeName}`;
  const { error } = await supabase.storage.from(bucket).upload(path, file, {
    cacheControl: '3600',
    contentType: file.type || 'application/octet-stream',
    upsert: false,
  });
  if (error) throw error;
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return normalizeStorageUrl(data.publicUrl);
};

export const uploadImage = (file, folder) => uploadFile(file, folder, MEDIA_BUCKET);

export const storagePathFromUrl = (url, bucket = MEDIA_BUCKET) => {
  if (!url) return null;
  const clean = url.split('?')[0];
  const markers = [
    `/object/public/${bucket}/`,
    `/object/sign/${bucket}/`,
    `/object/${bucket}/`,
  ];
  for (const marker of markers) {
    const idx = clean.indexOf(marker);
    if (idx !== -1) {
      const path = clean.slice(idx + marker.length);
      try {
        return decodeURIComponent(path);
      } catch (error) {
        return path;
      }
    }
  }
  return null;
};

export const removeStoredFile = async (url, bucket = MEDIA_BUCKET) => {
  const path = storagePathFromUrl(url, bucket);
  if (!path) return;
  await supabase.storage.from(bucket).remove([path]);
};

// Files in the private applications bucket are opened with a short lived
// signed link instead of a public URL.
export const createSignedUrl = async (url, bucket = APPLICATIONS_BUCKET, expiresIn = 3600) => {
  const path = storagePathFromUrl(url, bucket);
  if (!path) return null;
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, expiresIn);
  if (error) throw error;
  return data.signedUrl;
};
